import { useCompany, useGetTimeZones } from '@dispatch/shared/hooks';
import {
  AssignmentCategoryOptions,
  AttributeOptions,
  FNOL_RequestForm,
  FNOL_TaskTypeOptions,
  FNOL_TaskTypes,
  MultiSelectOption,
  SeverityOptions,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  AsyncSelect,
  CheckBoxField,
  DateTimeField,
  Section,
  SelectField,
  SubSection,
  TextField,
} from '@dispatch/web/ui';
import { Divider, Grid } from '@mui/material';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { FNOLClaimAttributesSection } from './FNOLClaimAttributes/FNOLClaimAttributesSection';
import { useEffect, useMemo } from 'react';
import { useGeneralDataContext } from '@dispatch/shared/hooks';

export type FNOLRequestSectionProps = {
  control: Control<FieldValues>;
  errors: FieldErrors;
  setResourceInput: React.Dispatch<React.SetStateAction<string>>;
  activeResources?: MultiSelectOption[];
  companyAttributes: AttributeOptions[];
  watch: UseFormWatch<FNOL_RequestForm>;
  getValues: UseFormGetValues<FNOL_RequestForm>;
  setValue: UseFormSetValue<FNOL_RequestForm>;
  setSelectedSlot: React.Dispatch<React.SetStateAction<number | null>>;
};

export const FNOLRequestSection = ({
  control,
  errors,
  setResourceInput,
  activeResources,
  companyAttributes,
  watch,
  getValues,
  setValue,
  setSelectedSlot,
}: FNOLRequestSectionProps) => {
  const { tenantId } = useCurrentUser();
  const { statesOptions, methodOfInspectionOptions } = useGeneralDataContext();
  const { perilOptions } = useCompany();
  const { timeZones } = useGetTimeZones(tenantId || '');
  const isNonComplex = watch('fnolTaskType') === FNOL_TaskTypes.NON_COMPLEX;
  const isComplex = watch('fnolTaskType') === FNOL_TaskTypes.COMPLEX;

  useEffect(() => {
    setSelectedSlot(null);
  });

  useEffect(() => {
    if (!isNonComplex) {
      setValue('durationInMinutes', 0);
    } else {
      setValue('durationInMinutes', 90);
    }
  }, [isNonComplex, setValue]);

  const preferredLanguages = useMemo(
    () =>
      companyAttributes
        .find((attribute) => attribute.title === 'Languages Spoken')
        ?.attributeDefinitionValues.map((value) => ({
          title: value.title,
          value: value.value,
        })),
    [companyAttributes]
  );

  const renderServiceDays = (days: string[]) =>
    days.map((day: string, index: number) => {
      return (
        <Grid item xs={3} key={day + String(index)}>
          <CheckBoxField
            control={control}
            id={`on${day})}`}
            name={`on${day}`}
            label={day}
          />
        </Grid>
      );
    });

  const renderClaimAttributes = () => {
    return (
      <FNOLClaimAttributesSection
        control={control}
        watch={watch}
        getValues={getValues}
        companyAttributes={companyAttributes}
      />
    );
  };

  return (
    <Section title="FNOL Request">
      <SubSection title={'TimeZone'}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <SelectField
              id="timeZone"
              name="timeZone"
              label="Time Zone"
              control={control}
              errors={errors}
              options={timeZones || []}
              required={isNonComplex}
            />
          </Grid>
        </Grid>
      </SubSection>
      <SubSection title="Location Information">
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="streetLine1"
              name="lossLocation.streetLine1"
              label="Street Line 1"
              control={control}
              errors={errors['lossLocation']}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="streetLine2"
              name="lossLocation.streetLine2"
              label="Street Line 2"
              control={control}
              errors={errors['lossLocation']}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="city"
              name="lossLocation.city"
              label="City"
              control={control}
              errors={errors['lossLocation']}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <SelectField
              id="state"
              name="lossLocation.state"
              label="State"
              control={control}
              errors={errors['lossLocation']}
              options={statesOptions}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              id="zip"
              name="lossLocation.zip"
              label="Zip"
              control={control}
              errors={errors['lossLocation']}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <SubSection title="Information">
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={3}>
            <DateTimeField
              id="startTime"
              name="startTime"
              label="Start Date Time"
              control={control}
              errors={errors}
              required={isNonComplex}
            />
          </Grid>
          <Grid item xs={3}>
            <DateTimeField
              id="endTime"
              name="endTime"
              label="End Date Time"
              control={control}
              errors={errors}
              required={isNonComplex}
            />
          </Grid>
          <Grid container item xs={6} alignItems="center" spacing={1}>
            {renderServiceDays([
              'Sunday',
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
            ])}
          </Grid>
        </Grid>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={3}>
            <SelectField
              id="fnolTaskType"
              name="fnolTaskType"
              label="Task Type"
              control={control}
              errors={errors}
              options={FNOL_TaskTypeOptions}
              required
            />
          </Grid>
          <Grid item xs={3}>
            <SelectField
              id="methodOfInspection"
              name="methodOfInspection"
              label="Method of Inspection"
              control={control}
              errors={errors}
              options={methodOfInspectionOptions || []}
              required={!isComplex}
            />
          </Grid>
          <Grid item xs={3}>
            <SelectField
              id="assignmentCategory"
              name="assignmentCategory"
              label="Assignment Category"
              control={control}
              errors={errors}
              options={AssignmentCategoryOptions}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              id="catCode"
              name="catCode"
              label="CAT Code"
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              id="durationInMinutes"
              name="durationInMinutes"
              label="Duration in Minutes"
              type={'number'}
              control={control}
              errors={errors}
              required={isNonComplex}
            />
          </Grid>
          <Grid item xs={3}>
            <SelectField
              id="severity"
              name="severity"
              label="Severity"
              control={control}
              errors={errors}
              options={SeverityOptions}
              required
            />
          </Grid>
          <Grid item xs={3}>
            <SelectField
              id="peril"
              name="peril"
              label="Peril"
              control={control}
              errors={errors}
              options={perilOptions || []}
              required
            />
          </Grid>
          <Grid item xs={3}>
            <SelectField
              id="preferredLanguage"
              name="preferredLanguage"
              label="Preferred Language"
              control={control}
              errors={errors}
              options={preferredLanguages || []}
            />
          </Grid>
          <Grid item xs={12}>
            <AsyncSelect
              id={'resourceIds'}
              name={'resourceIds'}
              label={'Request Resource(s)'}
              control={control}
              errors={errors}
              options={activeResources || []}
              setInput={setResourceInput}
              multiple={true}
            />
          </Grid>
        </Grid>
      </SubSection>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      {renderClaimAttributes()}
    </Section>
  );
};
